import { IAdvertiser, IAdvertiserManagers, IAffiliate, IContractor, IContractorAddress, ITag } from "@/api/types/revenue";
import { IUser } from "@/api/types/user";
import {
    addAdvertiserAddress,
    addAdvertiserManager,
    addContractor, currenciesChangeRequest,
    deleteAdvertiserManager,
    deleteContractor, deletePartnerAccounts,
    editAdvertiserAddress,
    getAdvertiserTags,
    patchAdvertisersTags,
    postPartnerAccounts, projectsChangeRequest,
    removeAdvertiserAddress,
    setDefaultContractor,
} from "@/api/revenue";
import { delay, showNotification, showServerError } from "@/utils";
import { eventBus } from "@/eventbus";
import isEqual from "lodash-es/isEqual";
import { patchAdvertiserEmailLanguage, patchAdvertiserInfo } from "@/api/offers";
import type { AdvertiserEmailLanguage } from "@/api/offers";
import { isAdvertiserSendingInvoicesTo1C } from "@/services/advertiser/advertiserSendsInvoicesTo1C";

export interface IProject {
    abbr: string,
    text: string
}

export class Advertiser implements IAdvertiser {
    id: number;
    name: string;
    status: string;
    internal_id: number;
    is_checker_used?: boolean;
    tag: ITag;
    currency?: string[];
    projects?: string[];
    invoicing_dates?: number[] | null;
    managers: IAdvertiserManagers[];
    contractors?: IContractor[];
    account_manager: IUser | null;
    affiliates?: IAffiliate[] = [];
    allTags: ITag[] = [];
    vat: boolean;
    email_language: string;
    note: string;
    send_invoices_to_1c: boolean;

    constructor(advertiser: IAdvertiser) {
        this.id = advertiser.id;
        this.name = advertiser.name;
        this.status = advertiser.status;
        this.internal_id = advertiser.internal_id;
        this.is_checker_used = advertiser.is_checker_used;
        this.tag = advertiser.tag;
        this.currency = advertiser.currency;
        this.projects = advertiser.projects;
        this.invoicing_dates = advertiser.invoicing_dates;
        this.managers = advertiser.managers;
        this.contractors = advertiser.contractors;
        this.account_manager = advertiser.account_manager;
        this.affiliates = advertiser.affiliates;
        this.vat = advertiser.vat;
        this.email_language = advertiser.email_language || 'ru';
        this.note = advertiser.note;
        this.send_invoices_to_1c = isAdvertiserSendingInvoicesTo1C(advertiser);

        (async () => {
            await this.getTags();
        })();
    }

    async getTags(): Promise<void> {
        try {
            const { data } = await getAdvertiserTags();
            this.allTags = data;
        } catch (err) {
            showServerError(err, 'Список тегов не загружен');
        }
    }

    async editMainInfo(advertiserData: any): Promise<void> {
        const changedOptions: string[] = [];
        const apis = {
            tag: patchAdvertisersTags,
            projects: projectsChangeRequest,
            currency: currenciesChangeRequest,
        };
        const mainInfoApi: {keys: string[], api: Function, data: any} = {
            keys: [ 'manager_id', 'name', 'status', 'vat', 'is_checker_used', 'note', 'send_invoices_to_1c' ],
            api: patchAdvertiserInfo,
            data: {},
        };

        const managerIdToNumber = (value: unknown): number | null => {
            if (value === '' || value == null) return null;
            const n = Number(value);
            return Number.isInteger(n) && n > 0 ? n : null;
        };

        const isNotChanged = (key: string) => {
            if (key === 'tag') return advertiserData[key] === this[key].id;
            if (key === 'email_language') {
                const next = advertiserData[key] || 'ru';
                const prev = this.email_language || 'ru';
                return next === prev;
            }
            if (key === 'manager_id') {
                const nextId = managerIdToNumber(advertiserData[key]);
                const prevId = managerIdToNumber(this.account_manager?.id);
                return nextId === prevId;
            }
            if (Array.isArray(advertiserData[key])) return isEqual(this[key], advertiserData[key]);
            return this[key] === advertiserData[key];
        };

        for (const key in advertiserData) {
            if (isNotChanged(key)) continue;
            if (key === 'email_language') continue;
            if (mainInfoApi.keys.includes(key)) {
                if (key === 'manager_id') {
                    const id = managerIdToNumber(advertiserData[key]);
                    if (id != null) mainInfoApi.data[key] = id;
                } else {
                    mainInfoApi.data[key] = advertiserData[key];
                }
                continue;
            }
            changedOptions.push(key);
        }

        const emailLanguageChanged = Object.prototype.hasOwnProperty.call(advertiserData, 'email_language')
            && !isNotChanged('email_language');

        if (changedOptions.length === 0 && !Object.keys(mainInfoApi.data).length && !emailLanguageChanged) return;

        try {
            if (!!changedOptions.length)
                await Promise.all(changedOptions.map(key => apis[key](this.id, advertiserData[key])));
            if (!!Object.keys(mainInfoApi.data).length)
                await mainInfoApi.api(mainInfoApi.data, this.id);
            if (emailLanguageChanged) {
                await patchAdvertiserEmailLanguage(this.id, {
                    email_language: advertiserData.email_language as AdvertiserEmailLanguage,
                });
            }
            showNotification('Данные рекламодателя обновлены');
            await delay(1000);
            eventBus.$emit("update-advertiser");
        } catch (err) {
            showServerError(err, 'Не удалось обновить данные');
        }
    }

    async addManager(manager_id: string): Promise<void> {
        try {
            await addAdvertiserManager(this.id, { manager_id });
            showNotification('Менеджер успешно добавлен');
            eventBus.$emit("update-advertiser");
        } catch (err) {
            showServerError(err, 'Не удалось добавить менеджера');
        }
    }

    async deleteManager(manager_id: number): Promise<void> {
        try {
            await deleteAdvertiserManager(this.id, manager_id);
            showNotification('Менеджер успешно удалён');
            eventBus.$emit("update-advertiser");
        } catch (err) {
            showServerError(err, 'Не удалось удалить менеджера');
        }
    }

    async setDefaultContractor(item: IContractor): Promise<void> {
        try {
            await setDefaultContractor(this.id, item.id);
            eventBus.$emit("update-advertiser");
        } catch (err) {
            showServerError(err, 'Не удалось установить контрагента по умолчанию');
        }
    }

    async addContractor(contractor_id: string): Promise<void> {
        try {
            await addContractor(this.id, contractor_id);
            showNotification('Контрагент успешно добавлен');
            eventBus.$emit("update-advertiser");
        } catch (err) {
            showServerError(err, 'Не удалось добавить контрагента');
        }
    }

    async deleteContractor(contractor_id: string): Promise<void> {
        try {
            await deleteContractor(this.id, contractor_id);
            showNotification('Контрагент успешно удалён');
            eventBus.$emit("update-advertiser");
        } catch (err) {
            showServerError(err, 'Не удалось удалить контрагента');
        }
    }

    async addOrEditAdvertiserAddress(advertiser_id: number, address: IContractorAddress, isEdit: boolean = false): Promise<void> {
        const apiMethod = isEdit ? editAdvertiserAddress : addAdvertiserAddress;
        const errText = isEdit ? 'изменить' : 'добавить';
        const successText = isEdit ? 'изменён' : 'добавлен';
        try {
            await apiMethod(this.id, advertiser_id, address);
            showNotification(`Адрес успешно ${successText}`);
            eventBus.$emit("update-advertiser");
        } catch (err) {
            showServerError(err, `Не удалось ${errText} адрес`);
        }
    }

    async deleteAddress(contractorId: number, addressId: number): Promise<void> {
        try {
            await removeAdvertiserAddress(this.id, contractorId, addressId);
            showNotification('Адрес успешно удалён');
            eventBus.$emit("update-advertiser");
        } catch (err) {
            showServerError(err, 'Не удалось удалить адрес');
        }
    }

    async addAffiliate(affiliate_id: number): Promise<void> {
        try {
            await postPartnerAccounts(this.id, { affiliate_id });
            showNotification('Партнёр успешно добавлен');
            eventBus.$emit("update-advertiser");
        } catch (err) {
            showServerError(err, 'Не удалось добавить партнёра');
        }
    }

    async deleteAffiliate(affiliate_id: number): Promise<void> {
        try {
            await deletePartnerAccounts(this.id, affiliate_id);
            showNotification('Партнёр успешно удалён');
            eventBus.$emit("update-advertiser");
        } catch (err) {
            showServerError(err, 'Не удалось удалить партнёра');
        }
    }
}

export const PROJECTS: IProject[] = [
    {
        abbr: '12',
        text: 'ВГО',
    },
    {
        abbr: 'IVR',
        text: 'IVR Амирова',
    },
    {
        abbr: 'LE',
        text: 'Лидгид Европа',
    },
    {
        abbr: 'LKz',
        text: 'Лидгид Казахстан',
    },
    {
        abbr: 'LR',
        text: 'Лидгид рубли',
    },
    {
        abbr: 'LU',
        text: 'Лидгид Украина (Гривны)',
    },
    {
        abbr: 'WO',
        text: 'Вебофис',
    },
    {
        abbr: 'WV',
        text: 'Вебворк',
    },
];
